import React from 'react';
import PropTypes from 'prop-types';
import {
  Switch,
  Route,
  Redirect,
  withRouter,
} from 'react-router-dom';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import _ from 'lodash';
import {
  Tabs,
  TabList,
  Tab,
} from '@pearsontechnology/aero-components';

import {
  PageWrapper,
  PageViewport,
} from 'components/PageStyles';

import Sidebar from 'Workspace/components/WorkspaceSidebar';
import CohortNavigation from 'Workspace/Cohorts/components/CohortNavigation';
import cohortShape from 'Workspace/types/cohorts/cohort';
import enhanceWithCohort from 'hocs/enhanceWithCohort';
import { featureKeys } from 'services/FeatureFlagService';
import enhanceWithFeatureFlagSubscription from "hocs/enhanceWithFeatureFlagSubscription";
import gtmService from 'utils/gtm/gtmService';
import { GTM_EVENT_TYPES } from 'utils/gtm/constants';

import CohortPageHeader from '../CohortPageHeader';
import ProgressGridContainer from './ProgressGridContainer';
import CompetencyGridContainer from './CompetencyGridContainer';
import GradesGridContainer from './GradesGridContainer';
import GradesGridNew from './GradesGridNew';

import messages from './messages';
import cohortPattern from './images/class-pattern-1.svg';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { commandRevokePermissionOnCohort,
} from 'containers/ConnectionManager/actions';

let paths;
let defaultTabIndex;

class CohortGrades extends React.Component {
  constructor(props) {
    super(props);

    const { location, match: { params: { cohortId, deploymentId } }, getFeatureFlagValue } = this.props;
    const isNewDesignEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_GRADES_NEW_DESIGN);
    if (isNewDesignEnabled) {
      paths = [
        'grades',
        'progress',
        'competency',
      ];
      defaultTabIndex = 0;
    } else {
      paths = [
        'progress',
        'competency',
        'grades',
      ];
      defaultTabIndex = 2;
    }
    const urlFragment = `/classes/${cohortId}/grades`;
    const pathUrls = paths.map((path) => `${urlFragment}/${deploymentId}/${path}`);
    const initialPathIndex = pathUrls.findIndex((path) => location.pathname.includes(path));

    this.state = {
      pageIndex: initialPathIndex !== -1 ? initialPathIndex : defaultTabIndex,
    };
  }

  componentDidMount() {
    if (this.props.location) {
      const { pathname } = this.props.location;
      gtmService.gtmTrackEvent({ eventType: GTM_EVENT_TYPES.PAGEVIEW, payload: { pageName: pathname } });
    }
  }

  shouldComponentUpdate = (nextProps) => (
    this.props.cohort !== nextProps.cohort
    || !_.isEqual(this.props.cohort.summary, nextProps.cohort.summary)
    || this.props.match.params.cohortId !== nextProps.match.params.cohortId
    || this.props.match.params.deploymentId !== nextProps.match.params.deploymentId
    || this.props.location.pathname !== nextProps.location.pathname
  );

  render() {
    const { pageIndex } = this.state;
    const { cohort, match: { params: { cohortId, deploymentId }, url }, history, getFeatureFlagValue, revokePermission } = this.props;
    const isNewDesignEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_GRADES_NEW_DESIGN);
    const urlFragment = `/classes/${cohortId}/grades`;

    if (!deploymentId && cohort && cohort.deployments && cohort.deployments.length > 0) {
      return <Redirect to={`${urlFragment}/${cohort.deployments[0].deploymentId}/grades`} />;
    }
    // TODO: should probably handle no deployments at some point in the near future.
    // perhaps with a message that says "no deployments to grade"

    const deployment = cohort.deployments && cohort.deployments.find((d) => d.deploymentId === deploymentId);

    if (isNewDesignEnabled && !(cohort.settings && cohort.settings.bannerPattern)) {
      cohort.settings = cohort.settings || {};
      cohort.settings.bannerPattern = cohortPattern;
    }

    const setPageIndex = (index) => {
      const pathUrls = paths.map((path) => `${urlFragment}/${deploymentId}/${path}`);
      this.setState({ pageIndex: index });
      history.push(pathUrls[index]);
    };

    const NavigatorActions = (
      <Tabs index={pageIndex} onChange={setPageIndex}>
        {!isNewDesignEnabled
          ? (
            <TabList>
              <Tab>Progress</Tab>
              <Tab>Competency</Tab>
              <Tab>Grades & Manual Grading</Tab>
            </TabList>
          ) : (
            <TabList>
              <Tab>Grades & Manual Grading</Tab>
              <Tab>Progress</Tab>
              <Tab>Competency</Tab>
            </TabList>
          )
        }
      </Tabs>
    );

    return (
      <PageWrapper>
        <Sidebar navigation={<CohortNavigation cohortId={cohortId} selectedKey="grades" />} />
        <PageViewport displayFlexColumn={isNewDesignEnabled} style={{ position: 'relative', overflowX: 'hidden' }}>
          {cohort && cohort.summary && (
            <React.Fragment>
              <CohortPageHeader
                cohort={cohort}
                title={intl.formatMessage(messages.title)}
                updateCohort={this.props.updateCohort}
                style={isNewDesignEnabled ? { flex: 'none' } : undefined}
                onRevoke={(account)=>{revokePermission(account, cohort.summary.cohortId);}}

              />
              { deployment && (
                <Switch>
                  <Route
                    path={`${url}/progress/:activityId`}
                    render={({ match }) => (
                      <ProgressGridContainer
                        cohortId={cohortId}
                        deployment={deployment}
                        selectedActivityId={match.params.activityId}
                        navigationActions={NavigatorActions}
                      />
                    )}
                  />
                  <Route
                    path={`${url}/progress`}
                    render={() => (
                      <ProgressGridContainer
                        cohortId={cohortId}
                        deployment={deployment}
                        navigationActions={NavigatorActions}
                      />
                    )}
                  />
                  <Route
                    path={`${url}/competency/:documentId/:documentItemId`}
                    render={({ match }) => (
                      <CompetencyGridContainer
                        cohortId={cohortId}
                        deployment={deployment}
                        documentId={match.params.documentId}
                        documentItemId={match.params.documentItemId}
                        navigationActions={NavigatorActions}
                      />
                    )}
                  />
                  <Route
                    path={`${url}/competency`}
                    render={() => (
                      <CompetencyGridContainer
                        cohortId={cohortId}
                        deployment={deployment}
                        navigationActions={NavigatorActions}
                      />
                    )}
                  />
                  <Route
                    path={`${url}/grades/:activityId`}
                    render={({ match }) => (
                      !isNewDesignEnabled ?
                        <GradesGridContainer
                          cohortId={cohortId}
                          deployment={deployment}
                          selectedActivityId={match.params.activityId}
                          navigationActions={NavigatorActions}
                        /> :
                        <GradesGridNew
                          cohortId={cohortId}
                          deployment={deployment}
                          selectedActivityId={match.params.activityId}
                          navigationActions={NavigatorActions}
                        />
                    )}
                  />
                  <Route
                    path={`${url}/grades`}
                    render={() => (
                      !isNewDesignEnabled ?
                        <GradesGridContainer
                          cohortId={cohortId}
                          deployment={deployment}
                          navigationActions={NavigatorActions}
                        /> :
                        <GradesGridNew
                          cohortId={cohortId}
                          deployment={deployment}
                          navigationActions={NavigatorActions}
                        />
                    )}
                  />
                </Switch>
              )}
            </React.Fragment>
          )}
        </PageViewport>
      </PageWrapper>
    );
  }
}

CohortGrades.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      cohortId: PropTypes.string.isRequired,
      deploymentId: PropTypes.string,
      activityId: PropTypes.string,
    }),
    url: PropTypes.string,
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  cohort: cohortShape,
  updateCohort: PropTypes.func.isRequired,
  history: PropTypes.object,
};


const mapDispatchToProps = (dispatch) => ({
  revokePermission: (account, cohortId) => dispatch(commandRevokePermissionOnCohort(account.id, account.type, cohortId)),
});

const flagNames = [featureKeys.WORKSPACE_GRADES_NEW_DESIGN];
const withCohort = enhanceWithCohort(({ match }) => match.params.cohortId);
export default connect(null, mapDispatchToProps)(compose(withCohort, withRouter, enhanceWithFeatureFlagSubscription)(CohortGrades, flagNames));
